import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import DriverSearchPanel from "@/components/DriverSearchPanel";
import { apiError } from "@/lib/errors";
import { api } from "@/lib/api";
import MedicalExpiryAcknowledgmentModal from "@/components/rc10m/MedicalExpiryAcknowledgmentModal";
import DriverCannotBeAssignedDialog from "@/components/rc10s/DriverCannotBeAssignedDialog";
import {
  preflight as rc10sPreflight,
  parseSaveErrorAsDiagnostic,
  newLocalCorrelationId,
} from "@/lib/driverAssignmentPreflight";

/**
 * RC10l · Driver picker modal — Replace / Fill-In flows.
 *
 * Shared modal used by the Coverage Needed card to swap or augment
 * drivers on a Tour with existing RC10l coverage warnings. Reuses
 * `DriverSearchPanel` so the eligibility + RC10l badges rendered by
 * the search are identical to the standalone Find Drivers surface.
 *
 * mode === "replace" calls PATCH /api/tours/{tour}/drivers/{assn}
 *   with { driver_id, warnings_acknowledged: true }. The backend
 *   preserves role, dates, and asset link.
 *
 * mode === "fill-in" calls POST /api/tours/{tour}/drivers with
 *   { driver_id, role, start_date, end_date, warnings_acknowledged,
 *     coverage_fill_in: true }. The additive flag is passed through
 *   so a future RC backend can discriminate — the current RC10l
 *   backend will accept-and-ignore extra keys. The original
 *   unavailable-flagged assignment is NOT touched.
 */
export default function DriverPickerModal({
  mode,                    // "replace" | "fill-in"
  tour,
  assignment,              // the original coverage-needed assignment
  onClose,
  onDone,                  // async () => void — refresh parent on success
}) {
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const isReplace = mode === "replace";
  // RC10m — ack-required 409 pending state
  const [medicalAck, setMedicalAck] = useState(null);
  const [medicalBusy, setMedicalBusy] = useState(false);

  // RC10s · diagnostic dialog state. Shared correlation ID per driver
  // selection family.
  const [diagnostic, setDiagnostic] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const correlationRef = useRef(null);
  const roleForPreflight =
    (assignment?.role === "co_driver" ? "co_driver" :
     (mode === "fill-in" ? "fill_in" : "primary_driver"));

  const title = isReplace ? "Find Replacement Driver" : "Assign Fill-In Driver";
  const subtitle = isReplace
    ? `Swap the current ${(assignment.role || "driver").replace(/_/g, " ")} on this Tour. Role, dates, and coach are preserved.`
    : `Add an additive coverage driver. The original assignment is kept.`;

  const confirmAction = async (row, extra = {}) => {
    if (inflight.current) return;
    const newDriverId = row?.driver?.id;
    if (!newDriverId) {
      toast.error("Select a driver first");
      return;
    }
    if (isReplace && newDriverId === assignment.driver_id) {
      toast.error("Replacement driver is the same as the current driver.");
      return;
    }
    inflight.current = true;
    setBusy(true);
    try {
      if (isReplace) {
        await api.patch(`/tours/${tour.id}/drivers/${assignment.id}`, {
          driver_id: newDriverId,
          warnings_acknowledged: true,
          ...extra,
        });
        toast.success(`${row.driver.display_name} assigned as replacement`);
      } else {
        await api.post(`/tours/${tour.id}/drivers`, {
          driver_id: newDriverId,
          role: assignment.role || "primary_driver",
          start_date: assignment.start_date || tour.start_date,
          end_date: assignment.end_date || tour.end_date,
          warnings_acknowledged: true,
          coverage_fill_in: true,
          ...extra,
        });
        toast.success(`${row.driver.display_name} assigned as fill-in`);
      }
      setMedicalAck(null);
      await onDone();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "medical_expiry_acknowledgment_required") {
        setMedicalAck({
          row,
          driverName: row?.driver?.display_name || newDriverId,
          roleLabel: ((assignment.role || "primary_driver").replace(/_/g, " ")),
          medicalExpiration: detail.medical_card_expiration,
          tourName: tour.display_name || tour.tour_number || "this Tour",
        });
      } else {
        // RC10s · structured diagnostic dialog replaces the raw
        // toast. Translates any server error shape into the shared
        // {blocking_reasons, warnings, correlation_id} envelope.
        const diag = parseSaveErrorAsDiagnostic(
          err, correlationRef.current?.correlationId,
        );
        setDiagnostic(diag);
      }
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };

  const confirmMedicalAck = async () => {
    if (!medicalAck?.row) return;
    setMedicalBusy(true);
    try {
      await confirmAction(medicalAck.row, { medical_expiry_acknowledged: true });
    } finally {
      setMedicalBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-carbon/60 flex items-start justify-center px-2 py-6 overflow-y-auto"
      data-testid={isReplace ? "driver-picker-modal-replace" : "driver-picker-modal-fill-in"}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-road-white rounded-md w-full max-w-5xl border border-coach-silver shadow-lg"
      >
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="flex items-baseline gap-3">
            <div className="text-[14px] font-medium" data-testid="driver-picker-title">
              {title}
            </div>
            <div className="text-[11px] text-coach-silver">
              {tour.tour_number ? `${tour.tour_number} · ` : ""}
              {tour.artist_display || tour.display_name || ""}
            </div>
            <div
              className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-hold/30 border border-amber-hold/50"
              data-testid="driver-picker-mode-indicator"
            >
              {isReplace ? "Mode: Replace" : "Mode: Fill-In"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="driver-picker-close"
            className="text-coach-silver hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[12px] text-steel">
            {subtitle} <span className="text-carbon font-medium">Role: {(assignment.role || "primary_driver").replace(/_/g, " ")}</span>
          </div>
          <DriverSearchPanel
            tourContext={{
              tour_id: tour.id,
              tour_number: tour.tour_number,
              customer_id: tour.customer_id,
              customer_display_name: tour.artist_display || tour.display_name,
              start_date: tour.start_date,
              end_date: tour.end_date,
            }}
            onRowAction={async (row) => {
              // RC10s · Preflight-on-select. Open the diagnostic
              // dialog if there is any blocking reason BEFORE
              // attempting the save. Preserves the user's picked row
              // for one-click correction.
              setSelectedRow(row);
              if (!correlationRef.current || correlationRef.current.driverId !== row?.driver?.id) {
                correlationRef.current = {
                  driverId: row?.driver?.id,
                  correlationId: newLocalCorrelationId(),
                };
              }
              const diag = await rc10sPreflight(
                tour.id, row?.driver?.id, roleForPreflight,
                correlationRef.current.correlationId,
              );
              correlationRef.current = {
                driverId: row?.driver?.id,
                correlationId: diag.correlation_id,
              };
              if ((diag.blocking_reasons || []).length > 0) {
                setDiagnostic(diag);
                return;
              }
              await confirmAction(row);
            }}
            actionLabel={busy ? "…" : (isReplace ? "Confirm Replacement" : "Assign as Fill-In")}
          />
          <div className="text-[11px] text-steel italic">
            Eligibility is enforced server-side on every action. Drivers with an overlapping
            Unavailable window will be rejected by the API.
          </div>
        </div>
      </div>
      <DriverCannotBeAssignedDialog
        open={!!diagnostic}
        diagnostic={diagnostic}
        driverId={selectedRow?.driver?.id}
        tourId={tour?.id}
        driverName={selectedRow?.driver?.display_name}
        onClose={() => setDiagnostic(null)}
        onFindReplacement={() => setDiagnostic(null)}
        onAcknowledgeWarning={async () => {
          setDiagnostic(null);
          if (selectedRow) await confirmAction(selectedRow, { warnings_acknowledged: true });
        }}
      />
      {medicalAck && (
        <MedicalExpiryAcknowledgmentModal
          driverName={medicalAck.driverName}
          roleLabel={medicalAck.roleLabel}
          medicalExpiration={medicalAck.medicalExpiration}
          tourName={medicalAck.tourName}
          busy={medicalBusy}
          onCancel={() => setMedicalAck(null)}
          onConfirm={confirmMedicalAck}
        />
      )}
    </div>
  );
}
